import React, { Fragment, useCallback, useRef, useState } from 'react';
import compact from 'lodash/compact';
import filter from 'lodash/filter';

import { SelectProductsModalButtonRequiredProps } from './SelectProductsModalButton.types';
import { ErrorMessage, I18nText } from '../../../types';
import { FetchProductsSetByUserIdScopeType } from '../../../main/productsSets/productsSetsTypes';
import { ProductsListOnProductsSelect } from '../../../main/products/components/lists/ProductsList';

import { useModal } from '../../modals/hooks/useModal';

import { SimpleModal } from '../../modals/SimpleModal';

import { ProductsIndexContent } from '../../../main/products/components/contents/ProductsIndexContent';
import { PureTooltipIconButtonHelper } from '../../buttons/PureTooltipIconButtonHelper';

import { ProductCache } from '../../../main/products/ProductCache';
import { ProductsSetCache } from '../../../main/productsSets/ProductsSetCache';

const defaultInitialFilters = { nda: false, blocked: false };
const SELECT_PRODUCTS_BUTTON = 'select-products-button';

interface SelectProductsModalButtonBaseProps {
  value?: string[];
  disabled?: boolean;
  submitErrorMessage?: ErrorMessage;
  productsSetScope?: FetchProductsSetByUserIdScopeType;
}

interface SelectProductsModalButtonWithTextProps {
  buttonText: string;
  i18nText?: never;
}

interface SelectProductsModalButtonWithI18nTextProps {
  buttonText?: never;
  i18nText: I18nText;
}

interface SelectProductsModalButtonWithOnSubmitProps {
  onSubmit: (value: string[]) => Promise<unknown>;
  onChange?: never;
}

interface SelectProductsModalButtonWithOnChangeProps {
  onSubmit?: never;
  onChange: (value: string[]) => void;
}

type SelectProductsModalButtonProps = SelectProductsModalButtonBaseProps &
  (
    | SelectProductsModalButtonWithTextProps
    | SelectProductsModalButtonWithI18nTextProps
  ) &
  (
    | SelectProductsModalButtonWithOnChangeProps
    | SelectProductsModalButtonWithOnSubmitProps
  );

function SelectProductsModalButton({
  value = [],
  disabled,
  onChange,
  onSubmit,
  className,
  i18nTitle,
  icon,
  iconClassName,
  modalIcon,
  i18nSubmitText,
  tooltipPlacement,
  tooltipI18nText,
  i18nText,
  buttonText,
  withoutInitialFocus,
  productsSetScope
}: SelectProductsModalButtonProps & SelectProductsModalButtonRequiredProps) {
  const selectedProductIds = useRef<string[]>([]);
  const attachProductIds = useRef<string[]>(value);
  const [imageLightboxOpen, setImageLightboxOpen] = useState<boolean>(false);

  const handleOpen = useCallback<() => void>(() => {
    attachProductIds.current = value;
    selectedProductIds.current = [];
  }, [value]);

  const handleClose = useCallback<() => void>(() => {
    const selectedProductsIds = filter(attachProductIds.current, (productId) =>
      selectedProductIds.current.includes(productId)
    );

    if (onChange) {
      onChange(selectedProductsIds);
    } else {
      return onSubmit?.(selectedProductsIds);
    }
  }, [onChange, onSubmit]);

  const { isOpen, showModal, hideModal } = useModal({
    onOpen: handleOpen,
    onClose: handleClose,
    preventModalClose: imageLightboxOpen
  });

  const handleProductsSelect = useCallback<ProductsListOnProductsSelect>(
    (selectedProducts) => {
      selectedProductIds.current = selectedProducts.map(
        (selectedProduct) => selectedProduct?.id
      );
    },
    []
  );

  const handleProductsAttach = useCallback<ProductsListOnProductsSelect>(
    (attachProducts) => {
      const selectedProductsIds = compact(
        attachProducts.map((selectedProduct) => selectedProduct.id)
      );

      attachProductIds.current = selectedProductsIds;

      hideModal();
    },
    [hideModal]
  );

  const togglePreventModalClose = useCallback<() => void>(
    () => setImageLightboxOpen((prevState) => !prevState),
    [setImageLightboxOpen]
  );

  return (
    <Fragment>
      {buttonText ? (
        <PureTooltipIconButtonHelper
          dataGa={SELECT_PRODUCTS_BUTTON}
          className={className}
          disabled={disabled}
          icon={icon}
          iconClassName={iconClassName}
          tooltipPlacement={tooltipPlacement}
          tooltipI18nText={tooltipI18nText}
          text={buttonText}
          onClick={showModal}
        />
      ) : (
        <PureTooltipIconButtonHelper
          dataGa={SELECT_PRODUCTS_BUTTON}
          className={className}
          disabled={disabled}
          icon={icon}
          iconClassName={iconClassName}
          tooltipPlacement={tooltipPlacement}
          tooltipI18nText={tooltipI18nText}
          i18nText={i18nText}
          onClick={showModal}
        />
      )}

      <SimpleModal
        isOpen={isOpen}
        hideModal={hideModal}
        childrenClassName="flex-1 flex overflow-hidden"
        i18nTitle={i18nTitle}
        modalIcon={modalIcon}
        modalSize="full"
        i18nSubmitText={i18nSubmitText}
        disabled={disabled}
        isLoading={disabled}
        withoutInitialFocus={withoutInitialFocus}
        withoutButtons
      >
        <ProductsIndexContent
          initialLimit={48}
          initialFilters={defaultInitialFilters}
          onProductsAttach={handleProductsAttach}
          onProductsSelect={handleProductsSelect}
          togglePreventModalClose={togglePreventModalClose}
          productsSetScope={productsSetScope}
          productsBaseCacheKey={ProductCache.indexCacheKey()}
          productsCacheKey={ProductCache.indexCacheKey()}
          productsSetCacheKey={ProductsSetCache.showCacheKey()}
          allowAiSearch
          allowElasticSearch
          isModal
        />
      </SimpleModal>
    </Fragment>
  );
}

export default SelectProductsModalButton;
